import { SortOrder } from '@/enums/SortOrder';
import qs from 'qs';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';

const initialState: TransportCostState = {
  all: [],
  viewing: undefined,
};

const getters: GetterTree<TransportCostState, RootState> = {
  all(state: TransportCostState): TransportCost[] {
    return state.all;
  },
  viewing(state: TransportCostState): TransportCost | undefined {
    return state.viewing;
  },
};

const mutations: MutationTree<TransportCostState> = {
  RESET(state: TransportCostState) {
    state.all = [];
  },
  ADD_MODEL(state: TransportCostState, payload: TransportCost) {
    state.all.push(payload);
  },
  ADD_MODELS(state: TransportCostState, payload: TransportCost[]) {
    state.all = [...state.all, ...payload];
  },
  SET_MODELS(state: TransportCostState, payload: TransportCost[]) {
    state.all = payload;
  },
  UPDATE_MODEL(state: TransportCostState, payload: TransportCost) {
    const index = state.all.findIndex((model: TransportCost) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: TransportCostState, payload: number) {
    state.all = state.all.filter((model: TransportCost) => model.id !== payload);
  },
  SET_VIEWING(state: TransportCostState, payload: TransportCost) {
    state.viewing = payload;
  },
};

const resource: string = 'transport-costs';

const actions: ActionTree<TransportCostState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, { data, workspaceId }: { data: TransportCostCreateData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: TransportCost }) => {
        commit('ADD_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index({ commit, rootState }, { query = { s: { created_at: SortOrder.DESC, id: SortOrder.ASC }, per_page: 20 }, workspaceId }: { query?: IndexQuery<TransportCost>; workspaceId: number }) {
    const queryString = qs.stringify(query, { encode: false });

    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: TransportCost[]; total: number }) => {
        if (query.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }
        return Promise.resolve(response);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: TransportCost }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { id, data, workspaceId }: { id: number; data: TransportCostUpdateData; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: TransportCost }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  patch({ commit, rootState }, { id, data, workspaceId }: { id: number; data: Partial<TransportCostUpdateData>; workspaceId: number }) {
    return rootState.api
      .patch(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: TransportCost }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: TransportCost }) => {
        commit('REMOVE_MODEL', id);

        return Promise.resolve(id);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  reset({ commit, rootState }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
